import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { axioKit } from "../../../../utilities";

const url = "/diagnostics/clinic/inventory";

const getClinicId = (inventory = {}) =>
  String(inventory?.clinic?._id || inventory?.clinic || "");

const getMenuId = (inventory = {}) =>
  String(inventory?.menu?._id || inventory?.menu || "");

const getAvailableUnits = (inventory = {}) => {
  const consumed = Array.isArray(inventory?.consumptions)
    ? inventory.consumptions.reduce(
        (sum, item) => sum + Number(item?.qnty || 0),
        0,
      )
    : 0;

  return Math.max(Number(inventory?.qnty || 0) - consumed, 0);
};

const getExpiryTime = (inventory = {}) => {
  const value = inventory?.expiry ? new Date(inventory.expiry).getTime() : 0;
  return Number.isFinite(value) ? value : 0;
};

const getCurrentMonth = () => new Date().getMonth() + 1;
const getCurrentYear = () => new Date().getFullYear();

export const buildInventoryCacheKey = (clinicIds = []) =>
  Array.from(
    new Set(
      (Array.isArray(clinicIds) ? clinicIds : []).map((id) => String(id || "")),
    ),
  )
    .filter(Boolean)
    .sort()
    .join(",");

const computeSummary = (collections = []) => {
  const today = new Date();
  const nextThirtyDays = new Date(today);
  nextThirtyDays.setDate(today.getDate() + 30);

  const menuTotals = new Map();
  let totalAvailableUnits = 0;
  let expiringSoonItems = 0;

  collections.forEach((item) => {
    const menuId = getMenuId(item);
    const available = getAvailableUnits(item);
    const expiryTime = getExpiryTime(item);

    totalAvailableUnits += available;

    if (menuId) {
      menuTotals.set(menuId, (menuTotals.get(menuId) || 0) + available);
    }

    if (
      available > 0 &&
      expiryTime &&
      expiryTime >= today.getTime() &&
      expiryTime <= nextThirtyDays.getTime()
    ) {
      expiringSoonItems += 1;
    }
  });

  let lowStockItems = 0;
  let outOfStockItems = 0;

  menuTotals.forEach((available) => {
    if (available <= 0) {
      outOfStockItems += 1;
      return;
    }

    if (available <= 5) {
      lowStockItems += 1;
    }
  });

  return {
    totalAvailableUnits,
    trackedProducts: menuTotals.size,
    lowStockItems,
    outOfStockItems,
    expiringSoonItems,
  };
};

const initialState = {
  collections: [],
  summary: {
    totalAvailableUnits: 0,
    trackedProducts: 0,
    lowStockItems: 0,
    outOfStockItems: 0,
    expiringSoonItems: 0,
  },
  lastFetchedAtByClinic: {},
  currentInventoryCacheKey: "",
  selectedMonth: getCurrentMonth(),
  selectedYear: getCurrentYear(),
  activeGroupIds: [],
  isLoading: false,
  isSuccess: false,
  message: "",
};

export const BROWSE = createAsyncThunk(
  `${url}/browse`,
  ({ token, key }, thunkAPI) => {
    try {
      return axioKit.universal(`${url}/browse`, token, key);
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();

      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const reduxSlice = createSlice({
  name: "clinicInventory",
  initialState,
  reducers: {
    SetInventoryCacheKey: (state, { payload }) => {
      state.currentInventoryCacheKey = String(payload || "");
    },
    SetSelectedMonth: (state, { payload }) => {
      state.selectedMonth = Number(payload || getCurrentMonth());
    },
    SetSelectedYear: (state, { payload }) => {
      state.selectedYear = Number(payload || getCurrentYear());
    },
    ToggleSupplyGroup: (state, { payload }) => {
      const groupId = String(payload || "");

      if (!groupId) return;

      state.activeGroupIds = state.activeGroupIds.includes(groupId)
        ? state.activeGroupIds.filter((id) => id !== groupId)
        : [...state.activeGroupIds, groupId];
    },
    SetActiveSupplyGroups: (state, { payload }) => {
      state.activeGroupIds = Array.from(
        new Set((Array.isArray(payload) ? payload : []).map(String).filter(Boolean)),
      );
    },
    HydrateInventoryConsumption: (state, { payload }) => {
      const clinicId = String(payload?.clinicId || "");
      const settlementId = payload?.settlementId || null;
      const cart = Array.isArray(payload?.cart) ? payload.cart : [];

      cart.forEach((item) => {
        const itemType = String(item?.itemType || "").toLowerCase();
        let remaining = Number(item?.qty || 0);
        const menuId = String(item?._id || item?.menu || "");
        const requestedDispenses = Array.isArray(item?.dispenses)
          ? item.dispenses
          : [];

        if (!clinicId || !menuId || !remaining || itemType !== "meds") return;

        if (requestedDispenses.length) {
          requestedDispenses.forEach((row) => {
            const inventoryId = String(
              row?.inventoryId || row?.inventory?._id || row?.inventory || "",
            );
            const requestedQty = Number(row?.qty || 0);
            if (!inventoryId || requestedQty <= 0 || remaining <= 0) return;

            const inventory = state.collections.find(
              (item) => String(item?._id || "") === inventoryId,
            );
            if (!inventory) return;

            const available = getAvailableUnits(inventory);
            if (!available) {
              inventory.status = "consumed";
              return;
            }

            const deduction = Math.min(available, requestedQty, remaining);
            if (!Array.isArray(inventory.consumptions)) {
              inventory.consumptions = [];
            }

            inventory.consumptions.push({
              stl: settlementId,
              qnty: deduction,
            });

            if (getAvailableUnits(inventory) <= 0) {
              inventory.status = "consumed";
            }

            remaining -= deduction;
          });
        }

        state.collections
          .filter(
            (inventory) =>
              getClinicId(inventory) === clinicId &&
              getMenuId(inventory) === menuId,
          )
          .sort((left, right) => {
            const expiryDiff = getExpiryTime(left) - getExpiryTime(right);
            if (expiryDiff !== 0) return expiryDiff;

            return (
              new Date(left?.createdAt || 0).getTime() -
              new Date(right?.createdAt || 0).getTime()
            );
          })
          .forEach((inventory) => {
            if (remaining <= 0) return;

            const available = getAvailableUnits(inventory);
            if (!available) {
              inventory.status = "consumed";
              return;
            }

            const deduction = Math.min(available, remaining);

            if (!Array.isArray(inventory.consumptions)) {
              inventory.consumptions = [];
            }

            inventory.consumptions.push({
              stl: settlementId,
              qnty: deduction,
            });

            if (getAvailableUnits(inventory) <= 0) {
              inventory.status = "consumed";
            }

            remaining -= deduction;
          });
      });

      state.summary = computeSummary(state.collections);
    },
    RESET: (state) => {
      state.message = "";
      state.isSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(BROWSE.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
        state.message = "";
      })
      .addCase(BROWSE.fulfilled, (state, action) => {
        const collections = Array.isArray(action.payload?.payload)
          ? action.payload.payload
          : [];
        const key = buildInventoryCacheKey(
          String(action.meta.arg?.key?.clinicIds || "")
            .split(",")
            .map((id) => id.trim())
            .filter(Boolean),
        );

        state.collections = collections;
        state.summary = computeSummary(collections);
        state.lastFetchedAtByClinic[key] = new Date().toISOString();
        state.isLoading = false;
        state.isSuccess = true;
        state.message = action.payload?.success || "";
      })
      .addCase(BROWSE.rejected, (state, action) => {
        state.isLoading = false;
        state.message = action.payload || action.error?.message || "";
      });
  },
});

export const {
  SetInventoryCacheKey,
  SetSelectedMonth,
  SetSelectedYear,
  ToggleSupplyGroup,
  SetActiveSupplyGroups,
  HydrateInventoryConsumption,
  RESET,
} = reduxSlice.actions;

export default reduxSlice.reducer;
